import { type AxialCoord, hexDistance, hexEquals, hexKey, hexNeighbors } from "../hex";
import { hexNoise, MAP_CENTRE, mirrorCoord } from "../map";
import { bestBy, rankBy } from "../pick";
import { nextInt, nextUint32, type RngState } from "../rng";
import type { RunRules } from "../types/rules";
import type { StructureKind } from "../types/state";

/**
 * Procgen of a new run's land, pure over coords, the rng and the rules: the starts (random edge pairs
 * or spread), rough ground and structures in twin pairs through the centre. A run with
 * `rules.starts` draws them in a symmetry group's orbits instead (`orbitPlacement.ts`, K5).
 */

/** Farthest hex from `from`, ties to insertion order; `from` itself for no hexes. */
function farthestFrom(hexes: readonly AxialCoord[], from: AxialCoord): AxialCoord {
  return bestBy(hexes, (coord) => hexDistance(coord, from)) ?? from;
}

export const PLACEMENT_TRIES = 200;

/** An edge hex of the blob: at most this many neighbours on the map. */
const EDGE_MAX_NEIGHBOURS = 4;

/** Procgen structures sit at least this far apart, and this far from their own twin. */
export const STRUCTURE_MIN_SPACING = 2;

/** Best-candidate placement: draws per structure pair, the farthest from everything placed wins. */
export const BEST_CANDIDATE_SAMPLES = 6;

export interface Starts {
  /** One start per kingdom in resolution order, the player's first; map coordinates. */
  readonly seats: readonly AxialCoord[];
  readonly rng: RngState;
}

/**
 * Start hexes, a twin pair through the map centre (Increment 3.18) so both factions see the
 * same land. The player's hex is drawn from the seeded stream among edge hexes until the pair
 * is at least `minStartDistance` apart (3.11, A16), at most `startRejectionLimit` times. The
 * fallback, and the whole rule at limit 0, is amendment A10: the edge hex farthest from the
 * centre and its twin, which is the hex farthest from it.
 */
export function pickStarts(
  hexes: readonly AxialCoord[],
  rng: RngState,
  rules: RunRules,
  factionCount: number,
): Starts {
  const index = new Set(hexes.map(hexKey));
  const edges = hexes.filter(
    (coord) =>
      hexNeighbors(coord).filter((n) => index.has(hexKey(n))).length <= EDGE_MAX_NEIGHBOURS,
  );
  const twinOf = (coord: AxialCoord): AxialCoord | undefined => {
    const twin = mirrorCoord(coord);
    return index.has(hexKey(twin)) && !hexEquals(twin, coord) ? twin : undefined;
  };
  let state = rng;
  for (let tries = 0; tries < rules.startRejectionLimit && edges.length >= 2; tries += 1) {
    const pick = nextInt(state, edges.length);
    state = pick.state;
    const player = edges[pick.value];
    const enemy = player && twinOf(player);
    if (player && enemy && hexDistance(player, enemy) >= rules.minStartDistance) {
      return { seats: spreadStarts([player, enemy], edges, hexes, factionCount), rng: state };
    }
  }
  const player = farthestFrom(hexes, MAP_CENTRE);
  const enemy = twinOf(player) ?? farthestFrom(hexes, player);
  return { seats: spreadStarts([player, enemy], edges, hexes, factionCount), rng: state };
}

/**
 * Kingdoms past the duel's twin pair (ADR-0012, until the authored map of W3): each takes the
 * edge hex farthest from every start already chosen, any hex when the edges run out. No draw,
 * so the duel's seeded stream is untouched. Ties keep insertion order.
 */
function spreadStarts(
  pair: readonly AxialCoord[],
  edges: readonly AxialCoord[],
  hexes: readonly AxialCoord[],
  factionCount: number,
): AxialCoord[] {
  const seats = [...pair];
  while (seats.length < factionCount) {
    const taken = new Set(seats.map(hexKey));
    const free = (pool: readonly AxialCoord[]): AxialCoord[] =>
      pool.filter((coord) => !taken.has(hexKey(coord)));
    const pool = free(edges).length > 0 ? free(edges) : free(hexes);
    const best = bestBy(pool, (coord) =>
      Math.min(...seats.map((seat) => hexDistance(seat, coord))),
    );
    if (!best) {
      throw new RangeError(`the map has no room for ${factionCount} kingdoms`);
    }
    seats.push(best);
  }
  return seats;
}

/**
 * Seeded land placement in map coordinates (Increment 3.18). Everything comes in twin pairs
 * through the centre, so neither side is handed a prize. Nothing lands on a start or next to
 * one. Rough ground takes the hexes where a seeded noise field is highest, so it clusters into
 * ridges with passes between them. Structures alternate city pair, well pair; each pair is
 * placed best-candidate style (`BEST_CANDIDATE_SAMPLES` draws, the one farthest from every
 * start and structure wins). An odd structure count puts one on the centre hex; an odd rough
 * count does the same when the centre is free.
 */
export function placeLand(
  coords: readonly AxialCoord[],
  starts: Starts,
  rules: RunRules,
  noiseScale: number,
): { rough: Set<string>; structures: Map<string, StructureKind>; rng: RngState } {
  const index = new Set(coords.map(hexKey));
  const anchors = starts.seats;
  const forbidden = new Set(
    coords.filter((coord) => anchors.some((start) => hexDistance(coord, start) <= 1)).map(hexKey),
  );
  const allowed = (coord: AxialCoord): boolean => {
    const key = hexKey(coord);
    return index.has(key) && !forbidden.has(key);
  };
  let state = starts.rng;

  const saltStep = nextUint32(state);
  state = saltStep.state;
  const ranked = rankBy(
    coords.filter(allowed),
    (coord) => -hexNoise(coord, noiseScale, saltStep.value),
  );
  const rough = new Set<string>();
  for (const coord of ranked) {
    if (rough.size >= rules.roughHexCount) {
      break;
    }
    const key = hexKey(coord);
    const twin = mirrorCoord(coord);
    const twinKey = hexKey(twin);
    if (rough.has(key) || !allowed(twin)) {
      continue;
    }
    if (hexEquals(twin, coord)) {
      // The centre is its own twin: it takes only an odd remainder, so pairs are never blocked.
      if ((rules.roughHexCount - rough.size) % 2 === 1) {
        rough.add(key);
      }
    } else if (rough.size + 2 <= rules.roughHexCount) {
      rough.add(key);
      rough.add(twinKey);
    }
  }

  const structures = new Map<string, StructureKind>();
  const placed: AxialCoord[] = [];
  const kindFor = (pairIndex: number): StructureKind => (pairIndex % 2 === 0 ? "city" : "well");
  const site = (coord: AxialCoord): boolean =>
    allowed(coord) &&
    !structures.has(hexKey(coord)) &&
    placed.every((placedCoord) => hexDistance(placedCoord, coord) >= STRUCTURE_MIN_SPACING);
  if (rules.structureCount % 2 === 1 && site(MAP_CENTRE)) {
    structures.set(hexKey(MAP_CENTRE), kindFor(Math.floor(rules.structureCount / 2)));
    placed.push(MAP_CENTRE);
  }
  let pairIndex = 0;
  for (
    let tries = 0;
    structures.size + 2 <= rules.structureCount && tries < PLACEMENT_TRIES;
    tries += 1
  ) {
    let best: AxialCoord | undefined;
    let bestSpread = -1;
    for (let sample = 0; sample < BEST_CANDIDATE_SAMPLES; sample += 1) {
      const pick = nextInt(state, coords.length);
      state = pick.state;
      const coord = coords[pick.value];
      if (!coord) {
        continue;
      }
      const twin = mirrorCoord(coord);
      if (hexDistance(coord, twin) < STRUCTURE_MIN_SPACING || !site(coord) || !site(twin)) {
        continue;
      }
      const spread = Math.min(
        ...[...anchors, ...placed].map((placedCoord) => hexDistance(placedCoord, coord)),
      );
      if (spread > bestSpread) {
        best = coord;
        bestSpread = spread;
      }
    }
    if (best) {
      const kind = kindFor(pairIndex);
      pairIndex += 1;
      for (const coord of [best, mirrorCoord(best)]) {
        structures.set(hexKey(coord), kind);
        placed.push(coord);
      }
    }
  }
  return { rough, structures, rng: state };
}
